// © Microsoft Corporation. All rights reserved.

import React, { CSSProperties } from "react";

import FocusLock from "react-focus-lock";
import { SocialClassNameContract } from "../../Social.classnames";
import { SocialState } from "../../Social.state";
import { connect } from "react-redux";
import { enums } from "../../utils/enums";
import { mouseHandlers } from "../../utils/mouseHandlers";

const mapStateToProps = function(state: SocialState) {
    return {
        responsiveClass: state.responsiveClass
    };
};

export interface PopoverProps {
    visible?: boolean;
    isHover?: boolean;
    triggerRef?: React.RefObject<any>;
    managedClasses?: SocialClassNameContract;
    onHide?: () => void;
    style?: CSSProperties;
    hasFocusLock?: boolean;
    className?: string;
    hideOnMobile?: boolean;
    responsiveClass?: string;
    focusLockStyle?: string;
    hoverDelay?: number;
    marginTop?: number;
    marginBottom?: number;
    onMoved?: () => void;
}

interface PopoverState {
    isHovered: boolean,
    delayCompleted: boolean
}

export class Popover extends React.Component<PopoverProps, PopoverState> {
    containerRef: React.RefObject<HTMLDivElement>;
    contentRef: React.RefObject<HTMLDivElement>;
    timeoutId: number;
    static hoverTriggeredPopover: Popover; // only one hover triggered popover should be rendered, this will hold the only instance
    shouldMove: boolean;

    constructor(props) {
        super(props);

        this.state = {
            isHovered: false,
            delayCompleted: false
        };

        this.containerRef = React.createRef();
        this.contentRef = React.createRef();
        this.onClickOutside = this.onClickOutside.bind(this);
        this.onLeaveTrigger = this.onLeaveTrigger.bind(this);
        this.escFunction = this.escFunction.bind(this);

        if (Popover.hoverTriggeredPopover && Popover.hoverTriggeredPopover != this) { Popover.hoverTriggeredPopover.hide(); }
        if (this.props.isHover) { Popover.hoverTriggeredPopover = this; }
        this.shouldMove = true;
    }

    escFunction(event) {
        if (event.keyCode === 27) {
            this.props.onHide();
        }
    }

    componentDidMount() {
        try {
            this.movePopover();
            document.addEventListener("click", this.onClickOutside);
            document.addEventListener("keydown", this.escFunction);
            window.addEventListener("scroll", this.props.onHide);

            if (this.props.isHover && this.props.triggerRef && this.props.triggerRef.current) {
                this.props.triggerRef.current.addEventListener("mouseleave", this.onLeaveTrigger);

                if (this.props.hoverDelay) { this.timeoutId = window.setTimeout(() => this.setState({delayCompleted: true}), this.props.hoverDelay); }
            }
        } catch (e) {
            // Suppress the error
        }
    }
    componentDidUpdate () {
        this.movePopover();
    }
    componentWillUnmount() {
        window.clearTimeout(this.timeoutId);
    }

    onClickOutside(event) {
        try {
            const clickedIsNotTrigger = (this.props.triggerRef &&
                this.props.triggerRef.current &&
                !this.props.triggerRef.current.contains(event.target)) || !this.props.triggerRef;

            const clickedIsNotChild = this.containerRef &&
                this.containerRef.current &&
                !this.containerRef.current.contains(event.target);

            if (clickedIsNotChild && clickedIsNotTrigger) {
                this.hide();
            }
        } catch (e) {
            // Suppress the error
        }
    }

    onClickInside(e) {
        e.stopPropagation();
    }

    onLeaveTrigger() {
        this.hide();
    }

    hide() {
        if (this.timeoutId) { window.clearTimeout(this.timeoutId); }
        if (Popover.hoverTriggeredPopover == this) { Popover.hoverTriggeredPopover = null; }

        if (this.props.isHover && !this.props.hoverDelay) {
            window.setTimeout(() => {
                try {
                    if (!mouseHandlers.isElementHover(this.props.triggerRef.current) &&
                        !mouseHandlers.isElementHover(this.containerRef.current)) {
                        this.props.triggerRef.current.removeEventListener("mouseleave", this.onLeaveTrigger);
                        this.props.onHide();
                    }
                } catch (e) {
                    // Suppress the error
                }
            }, 200);
        } else {
            this.props.onHide();
        }
    }

    movePopover() {
        if (!this.shouldMove || !this.contentRef || !this.contentRef.current) { return; }
        this.shouldMove = false;
        const {marginTop, marginBottom} = this.props;
        const popoverRect = (this.contentRef.current.firstChild as HTMLElement).getBoundingClientRect();
        const leftDiff = popoverRect.left;
        const rightDiff = popoverRect.right - document.documentElement.clientWidth;
        const topDiff = popoverRect.top;
        const botDiff = popoverRect.bottom - document.documentElement.clientHeight;
        if (topDiff<0 || botDiff>0 || leftDiff<0 || rightDiff>0) {
            const translateY = (topDiff < 0 ? topDiff-(marginTop || 10) : 0) + (botDiff > 0 ? botDiff+(marginBottom || 10) : 0);
            const translateX = (leftDiff < 0 ? leftDiff: 0) + (rightDiff > 0 ? rightDiff: 0);
            this.contentRef.current.style.transform = `translateX(${-translateX}px) translateY(${-translateY}px)`;
        }
        this.contentRef.current.setAttribute("data-show", "true");
        this.props.onMoved && this.props.onMoved();
    }

    render() {

        if (this.props.hoverDelay && (!this.state.delayCompleted && this.props.isHover)) { return null; }

        const { visible, children, isHover, managedClasses, hideOnMobile, responsiveClass, className, style, hasFocusLock } = this.props;
        const shouldHideMobile = hideOnMobile && responsiveClass && responsiveClass.includes(enums.responsiveLevels.MobilePopup);

        if (shouldHideMobile) { this.props.onHide(); }

        let content;
        const innerContent = <div className={managedClasses.popoverContainer} ref={this.containerRef} onClick={this.onClickInside}>
            <div className={`cwt-popover-content ${className}`} style={style} ref={this.contentRef} onMouseLeave={(isHover) ? this.hide.bind(this) : undefined}>
                {children}
            </div>
        </div>;

        if (hasFocusLock) {
            content = <FocusLock className={this.props.focusLockStyle}>
                {innerContent}
            </FocusLock>;
        } else {
            content = <React.Fragment>
                {innerContent}
            </React.Fragment>;
        }

        return (
            visible && !shouldHideMobile ? content  : ""
        );
    }
}

export default connect(mapStateToProps)(Popover);